import React, {useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {useRecoilState, useSetRecoilState} from 'recoil';
import makeStyles from '@mui/styles/makeStyles';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import {Person, ExitToApp, EditOutlined} from '@mui/icons-material';
import {getProfileImage} from '../../../../util/UserUtils';
import {userState} from '../../../../recoil/user/userAtom';
import {savedProjectsFilterState} from '../../../../recoil/filterValues/filterValuesAtom';
import * as Sentry from '@sentry/react';
import {signOut} from 'firebase/auth';
import {firebaseAuth} from '../../../../App';
import Paper from '@mui/material/Paper/Paper';

const useStyles = makeStyles((theme) => ({
  avatar: {
    objectFit: 'cover',
    cursor: 'pointer',

    '& img': {
      borderRadius: 30,
      border: `1px solid ${theme.palette.secondary.main}`,
    },
    '& :hover': {
      border: `1px ridge ${theme.palette.primary.main}`,
    },
    '& .MuiAvatar-fallback': {
      width: '100%',
      height: '100%',
      borderRadius: 30,
      padding: '6px',
      cursor: 'pointer',
    },
  },
  menuItemRoot: {
    marginBottom: 4,
  },
  listItemRoot: {
    color: theme.palette.primary.main,
  },
  listItemTextPrimary: {
    color: theme.palette.primary.main,
    fontSize: '14px',
    lineHeight: 0,
    marginLeft: 6,
  },
}));

function UserProfileAvatarMenu(props: $TSFixMe) {
  const anchorRef = useRef(null);
  const classes = useStyles();
  const history = useHistory();
  const [user, setUser] = useRecoilState(userState);
  const [menuOpen, setMenuOpen] = useState(false);
  const setSavedProjectsFilter = useSetRecoilState(savedProjectsFilterState);

  const toggleMenuOpen = () => {
    setMenuOpen(!menuOpen);
  };

  const goToUrl = (url: $TSFixMe) => {
    history.push(url);
    toggleMenuOpen();
  };

  const handleClickLogout = () => {
    signOut(firebaseAuth)
      .then(() => {
        history.push('/');
        sessionStorage.clear();
        localStorage.clear();
        setUser(null as any);
        setSavedProjectsFilter(false);
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag(
            'section',
            'UserProfileAvatarMenu.tsx handleClickLogout()',
          );
          Sentry.captureException(err);
        });
      });
  };

  return (
    <>
      <Avatar
        ref={anchorRef}
        onClick={toggleMenuOpen}
        aria-label="userProfileAvatarMenu"
        className={classes.avatar}
        src={getProfileImage(user, '100x100')}
      />

      <Popper
        open={menuOpen}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal>
        {({TransitionProps, placement}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}>
            <Paper style={{marginRight: 8}}>
              <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
                <MenuList>
                  <MenuItem
                    id="primaryNavigationMenuProfile"
                    key="myProfile"
                    onClick={() => goToUrl(`/user/${user.id}`)}
                    classes={{root: classes.menuItemRoot}}>
                    <ListItemIcon classes={{root: classes.listItemRoot}}>
                      <Person />
                    </ListItemIcon>
                    <ListItemText
                      classes={{
                        primary: classes.listItemTextPrimary,
                      }}
                      primaryTypographyProps={{variant: 'h6'}}
                      primary="MY PROFILE"
                    />
                  </MenuItem>
                  <MenuItem
                    key="editProfile"
                    onClick={() => goToUrl('/my/profile/dashboard')}
                    classes={{root: classes.menuItemRoot}}>
                    <ListItemIcon classes={{root: classes.listItemRoot}}>
                      <EditOutlined />
                    </ListItemIcon>
                    <ListItemText
                      classes={{
                        primary: classes.listItemTextPrimary,
                      }}
                      primaryTypographyProps={{variant: 'h6'}}
                      primary="SETTINGS"
                    />
                  </MenuItem>
                  <MenuItem
                    key="logout"
                    onClick={handleClickLogout}
                    classes={{root: classes.menuItemRoot}}>
                    {' '}
                    <ListItemIcon classes={{root: classes.listItemRoot}}>
                      <ExitToApp />
                    </ListItemIcon>
                    <ListItemText
                      classes={{
                        primary: classes.listItemTextPrimary,
                      }}
                      primaryTypographyProps={{variant: 'h6'}}
                      primary="SIGN OUT"
                    />
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default UserProfileAvatarMenu;
